import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { CorporateHeroSectionSettings } from '../../../../types/corporateHero';
import { sanitizeRichText } from '../../../../utils/richText';

interface CorporateHeroPreviewProps {
  hero: CorporateHeroSectionSettings;
  titleFallback?: string;
  descriptionFallback?: string;
}

const RichText: React.FC<{ html: string; sx?: SxProps<Theme> }> = ({ html, sx }) => (
  <Typography
    component="div"
    sx={{
      '& p': { m: 0, mb: 0.5 },
      '& p:last-child': { mb: 0 },
      '& ul, & ol': { m: 0, pl: 2.5 },
      '& .rt-columns': { columnGap: '24px' },
      ...sx,
    }}
    dangerouslySetInnerHTML={{ __html: sanitizeRichText(html) }}
  />
);

const CorporateHeroPreview: React.FC<CorporateHeroPreviewProps> = ({
  hero,
  titleFallback = 'Başlık',
  descriptionFallback = 'Açıklama',
}) => {
  const titleColor = hero.design.titleColor || '#ffffff';
  const descriptionColor = hero.design.descriptionColor || 'rgba(255,255,255,0.9)';
  const previewHeight = hero.height || 500;

  const title = hero.title || titleFallback;
  const description = hero.description || descriptionFallback;

  const hasFeatureBlocks = !!(
    hero.layout.showPrimaryButtons ||
    hero.layout.showSecondaryButtons ||
    hero.layout.showStatsStrip ||
    hero.layout.showImageSlider
  );

  const renderCtaFeature = () => {
    const primaryButtons = (hero.layout.primaryButtons || []).filter(
      (b) => b.enabled !== false && b.label
    );
    const secondaryButtons = (hero.layout.secondaryButtons || []).filter(
      (b) => b.enabled !== false && b.label
    );
    const slides = (hero.layout.slides || []).filter((s) => s.imageUrl);
    const stats = (hero.layout.stats || []).filter((s) => s.value || s.label).slice(0, 4);
    const showSlider = hero.layout.showImageSlider && slides.length > 0;

    return (
      <Box
        sx={{
          width: '100%',
          minHeight: previewHeight,
          background: hero.backgroundColor,
          borderRadius: 2,
          border: '1px solid #ddd',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: showSlider ? 'row' : 'column',
            gap: 2,
            alignItems: 'center',
            flex: 1,
          }}
        >
          <Box
            sx={{
              flex: showSlider ? 1.2 : 1,
              width: '100%',
              textAlign: hero.design.textAlign || 'center',
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: titleColor, mb: 0.5, width: '100%' }}>
              {title}
            </Typography>
            <RichText html={description} sx={{ color: descriptionColor, fontSize: '0.75rem', mb: 1 }} />
            {hero.layout.showPrimaryButtons && primaryButtons.length > 0 && (
              <Box sx={{ display: 'flex', width: '100%', gap: 0.75, flexWrap: 'wrap', justifyContent: 'center' }}>
                {primaryButtons.map((btn, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'white',
                      color: '#1565C0',
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {btn.label}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          {showSlider && (
            <Box
              sx={{
                flex: 1,
                height: 120,
                borderRadius: 1,
                backgroundImage: `url(${slides[0].imageUrl})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            />
          )}
        </Box>
        {hero.layout.showSecondaryButtons && secondaryButtons.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {secondaryButtons.map((btn, i) => (
              <Typography key={i} sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 10, fontWeight: 700 }}>
                {btn.label}
              </Typography>
            ))}
          </Box>
        )}
        {hero.layout.showStatsStrip && stats.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.2)',
              p: 1,
            }}
          >
            {stats.map((stat, i) => (
              <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                <Typography sx={{ color: titleColor, fontWeight: 800, fontSize: 12 }}>
                  {stat.value || '—'}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 9 }}>
                  {stat.label || '—'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const renderClassic = () => {
    if (hasFeatureBlocks) return renderCtaFeature();

    return (
      <Box
        sx={{
          width: '100%',
          height: previewHeight,
          background: hero.backgroundColor,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 2,
          border: '1px solid #ddd',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, color: titleColor }}>
            {title}
          </Typography>
          <RichText html={description} sx={{ color: descriptionColor, fontSize: '0.875rem' }} />
        </Box>
      </Box>
    );
  };

  const renderSplit = () => (
    <Box
      sx={{
        width: '100%',
        height: previewHeight,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        border: '1px solid #ddd',
        flexDirection: hero.layout.imagePosition === 'left' ? 'row-reverse' : 'row',
      }}
    >
      <Box
        sx={{
          flex: 1,
          height: '100%',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: hero.design.textAlign === 'right' ? 'flex-end' : 'flex-start',
          textAlign: hero.design.textAlign === 'right' ? 'right' : 'left',
          bgcolor: hero.backgroundColor || '#f8fafc',
        }}
      >
        {hero.design.badgeText && (
          <Box sx={{ display: 'inline-block', alignSelf: hero.design.textAlign === 'right' ? 'flex-end' : 'flex-start', px: 1, py: 0.25, mb: 1, borderRadius: 2, bgcolor: hero.design.badgeColor || '#e2e8f0', color: hero.design.badgeTextColor || '#334155', fontSize: 10 }}>
            {hero.design.badgeText}
          </Box>
        )}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: hero.design.titleColor || '#1e293b', mb: 0.5, width: '100%', textAlign: 'inherit' }}>
          {title}
        </Typography>
        <RichText html={description} sx={{ color: hero.design.descriptionColor || '#64748b', fontSize: '0.75rem', width: '100%', textAlign: 'inherit' }} />
      </Box>
      <Box
        sx={{
          flex: 1,
          height: '100%',
          backgroundImage: hero.layout.imageUrl ? `url(${hero.layout.imageUrl})` : 'none',
          backgroundColor: '#e2e8f0',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    </Box>
  );

  const renderOverlay = () => {
    const overlayColor = hero.design.overlayColor || '#0f172a';
    const overlayOpacity = hero.design.overlayOpacity ?? 0.65;
    const gradientDirection =
      hero.design.textAlign === 'center'
        ? 'to top'
        : hero.design.textAlign === 'right'
          ? 'to top left'
          : 'to top right';

    return (
    <Box
      sx={{
        width: '100%',
        height: previewHeight,
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid #ddd',
        backgroundImage: hero.layout.backgroundImageUrl ? `url(${hero.layout.backgroundImageUrl})` : 'none',
        backgroundColor: '#cbd5e1',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: overlayColor,
          opacity: overlayOpacity,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(${gradientDirection}, ${overlayColor} 0%, transparent 72%)`,
          opacity: Math.min(overlayOpacity + 0.15, 0.95),
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: hero.design.textAlign === 'center' ? 'center' : hero.design.textAlign === 'right' ? 'flex-end' : 'flex-start',
          p: 2,
          pb: 2.5,
          textAlign: hero.design.textAlign || 'left',
        }}
      >
        <Box sx={{ maxWidth: '65%' }}>
          {hero.design.badgeText && (
            <Box sx={{ display: 'inline-block', px: 0.75, py: 0.25, mb: 0.75, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 9 }}>
              {hero.design.badgeText}
            </Box>
          )}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#ffffff', mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}>
            {title}
          </Typography>
          <RichText html={description} sx={{ color: 'rgba(255,255,255,0.92)', textShadow: '0 1px 8px rgba(0,0,0,0.3)', fontSize: '0.75rem' }} />
        </Box>
      </Box>
    </Box>
    );
  };

  const renderMinimal = () => (
    <Box
      sx={{
        width: '100%',
        height: previewHeight,
        background: hero.backgroundColor || '#fafafa',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 2,
        border: '1px solid #ddd',
      }}
    >
      <Box>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, color: hero.design.titleColor || '#1e293b' }}>
          {title}
        </Typography>
        <RichText html={description} sx={{ color: hero.design.descriptionColor || '#64748b', fontSize: '0.875rem' }} />
      </Box>
    </Box>
  );

  const renderTwoColumn = () => (
    <Box
      sx={{
        width: '100%',
        height: previewHeight,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        border: '1px solid #ddd',
        bgcolor: hero.backgroundColor || '#f0f7fa',
        p: 1.5,
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: hero.design.titleColor || '#0d9488', mb: 0.5 }}>
          {title}
        </Typography>
        <RichText html={description} sx={{ color: hero.design.descriptionColor || '#64748b', fontSize: 9 }} />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {[hero.layout.leftColumn, hero.layout.rightColumn].map((col, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              p: 1,
              borderRight: index === 0 ? '1px solid #cbd5e1' : 'none',
            }}
          >
            {col?.badge && (
              <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mb: 0.5, fontSize: 8 }}>
                {col.badge}
              </Typography>
            )}
            <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', color: '#1e293b', mb: 0.5 }}>
              {col?.title || (index === 0 ? 'Sol Başlık' : 'Sağ Başlık')}
            </Typography>
            {col?.description ? (
              <RichText html={col.description} sx={{ color: '#64748b', fontSize: 9 }} />
            ) : (
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: 9 }}>—</Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderThreeStats = () => {
    const stats = hero.layout.stats?.length ? hero.layout.stats : [{ value: '100+', label: 'Stat' }, { value: '500+', label: 'Stat' }, { value: '99%', label: 'Stat' }];
    const statsTitleColor = hero.design.titleColor || '#0d9488';
    const statsDescriptionColor = hero.design.descriptionColor || '#64748b';
    return (
      <Box
        sx={{
          width: '100%',
          height: previewHeight,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #ddd',
          background: hero.backgroundColor || '#f0f7fa',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: statsTitleColor, textAlign: 'center' }}>
          {title}
        </Typography>
        <RichText html={description} sx={{ color: statsDescriptionColor, textAlign: 'center', fontSize: 9, mb: 0.5 }} />
        <Box sx={{ display: 'flex', minHeight: 0 }}>
          {stats.slice(0, 3).map((stat, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: i < 2 ? '1px solid #cbd5e1' : 'none',
                px: 0.5,
              }}
            >
              <Typography variant="caption" fontWeight="bold" sx={{ color: statsTitleColor }}>
                {stat.value || '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: statsDescriptionColor, fontSize: 9 }}>
                {stat.label || '—'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  switch (hero.templateType) {
    case 'cta-feature':
      return renderCtaFeature();
    case 'split-text-image':
      return renderSplit();
    case 'image-overlay':
      return renderOverlay();
    case 'minimal-centered':
      return renderMinimal();
    case 'two-column-text':
      return renderTwoColumn();
    case 'three-column-stats':
      return renderThreeStats();
    case 'classic-gradient':
    default:
      return renderClassic();
  }
};

export default CorporateHeroPreview;
